/**
 * 知识点 Store — 驼峰统一状态
 */

import { create } from 'zustand';
import type { KnowledgeItem, KnowledgeType } from '@hump/shared-types';
import { knowledgeRepository } from '../lib/repository/knowledge';

interface KnowledgeState {
  items: KnowledgeItem[];
  loading: boolean;
  error: string | null;
  filter: KnowledgeType | 'all';
  searchQuery: string;

  load: () => Promise<void>;
  setFilter: (f: KnowledgeType | 'all') => void;
  setSearchQuery: (q: string) => void;
  remove: (id: string) => Promise<void>;

  /** 过滤后的列表 */
  filtered: () => KnowledgeItem[];
}

export const useKnowledgeStore = create<KnowledgeState>((set, get) => ({
  items: [],
  loading: false,
  error: null,
  filter: 'all',
  searchQuery: '',

  load: async () => {
    set({ loading: true, error: null });
    try {
      let items: KnowledgeItem[];
      const { searchQuery, filter } = get();
      if (searchQuery) {
        items = await knowledgeRepository.search(searchQuery);
      } else if (filter !== 'all') {
        items = await knowledgeRepository.getByType(filter);
      } else {
        items = await knowledgeRepository.getAll();
      }
      set({ items, loading: false });
    } catch (e) {
      set({ error: (e as Error).message, loading: false });
    }
  },

  setFilter: (f) => {
    set({ filter: f });
    get().load();
  },

  setSearchQuery: (q) => {
    set({ searchQuery: q });
    get().load();
  },

  remove: async (id) => {
    try {
      await knowledgeRepository.remove(id);
      set((state) => ({ items: state.items.filter((i) => i.id !== id) }));
    } catch (e) {
      set({ error: (e as Error).message });
    }
  },

  filtered: () => {
    const { items, filter, searchQuery } = get();
    let result = items;
    if (filter !== 'all') {
      result = result.filter((i) => i.type === filter);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter((i) => {
        const text = [i.word, i.text, i.note, ...(i.definitions ?? [])].join(' ').toLowerCase();
        return text.includes(q);
      });
    }
    return result;
  },
}));
